import { ChangeEvent, Dispatch, FunctionComponent, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Modal } from "@/components";
import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";

import { useCreatCustomColumnTemplateMutation } from "@/pages/Private/redux/column/column.api";
import {
	CreateColumnTemplate,
	CreateColumnTemplateSchema,
} from "@/pages/Private/pages/List/schema/column-template";
import { InputField } from "@/components/InputField/InputField";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { CustomColumnTypes, ListSourceType } from "@/enum/list.enum";

interface CreatePromptModalProps {
	isOpen: boolean;
	setIsOpen: Dispatch<SetStateAction<boolean>>;
}

const typeOptions: AutoCompleteItem[] = Object.values(CustomColumnTypes).map((type) => ({
	id: type,
	title: type,
}));

const sourceTypeOptions: AutoCompleteItem[] = Object.values(ListSourceType).map((type) => ({
	id: type,
	title: type,
}));

export const CreatePromptModal: FunctionComponent<CreatePromptModalProps> = ({
	isOpen,
	setIsOpen,
}) => {
	const { t } = useTranslation();
	const [createColumnTemplate, { isLoading, error }] = useCreatCustomColumnTemplateMutation();

	const {
		handleSubmit,
		setValue,
		watch,
		formState: { errors },
	} = useForm<CreateColumnTemplate>({
		defaultValues: {
			name: "",
			config: {},
			type: CustomColumnTypes.AI_PROMPT,
			generalTemplate: true,
		},
		resolver: zodResolver(CreateColumnTemplateSchema),
	});

	const formValues = watch();

	const formErrors = Object.values(errors).map((error) => error?.message) as ERROR_TYPE[];

	const onSubmit = async (values: CreateColumnTemplate) => {
		try {
			await createColumnTemplate(values).unwrap();
			setIsOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<Modal
			handleClose={() => setIsOpen(false)}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isLoading}
			isOpened={isOpen}
			submitButtonText={t("basics.confirm")}
			title={"Create Prompt Template"}
		>
			<div>
				<div className="flex items-center mb-1">
					<InputField
						error={!!errors.name?.message}
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setValue("name", e.target.value);
						}}
						label={"Name"}
						name={"name"}
						placeholder={"e.g. My Prompt Template"}
						value={formValues.name}
					/>
				</div>

				<div className="flex items-center mb-1">
					<Dropdown
						data={typeOptions}
						defaultValue={typeOptions.find((opt) => opt.id === formValues.type)}
						floating={true}
						handleSelect={(item) => {
							if (item) {
								setValue("type", item.id as CustomColumnTypes);
							}
						}}
						label={"Type"}
					/>
				</div>

				{renderErrorMessages(getAllErrors(error, formErrors))}
			</div>
		</Modal>
	);
};
